import React, { useState, useRef } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import { type RichTextEditorRef } from 'mui-tiptap';
import Editor from './editor/Editor';
import { tablePrimaryButtonSx } from '../../styles/tableButtonStyles';

interface EditTemplateModalProps {
  open: boolean;
  onClose: () => void;
  code: string;
  subject: string;
  body: string;
  signOff: string;
  onUpdate: (subject: string, body: string, signOff: string) => Promise<void>;
  error?: string;
}

const style = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 700,
  maxHeight: '90vh',
  overflow: 'auto',
  bgcolor: 'background.paper',
  border: '2px solid #1976d2',
  boxShadow: 24,
  p: 4,
};

export default function EditTemplateModal({ open, onClose, code, subject, body, signOff, onUpdate, error }: EditTemplateModalProps) {
  const [editSubject, setEditSubject] = useState(subject);
  const rteRef = useRef<RichTextEditorRef>(null);
  const signOffRef = useRef<RichTextEditorRef>(null);

  React.useEffect(() => {
    setEditSubject(subject);
  }, [open]);

  const handleUpdate = async () => {
    const editBody = rteRef.current?.editor?.getHTML() ?? '';
    const editSignOff = signOffRef.current?.editor?.getHTML() ?? '';
    await onUpdate(editSubject, editBody, editSignOff);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Typography variant='h6' mb={2}>
          Edit Email Template
        </Typography>

        <TextField label='Subject' fullWidth value={editSubject} onChange={(e) => setEditSubject(e.target.value)} margin='normal' />

        <Typography variant='body2' color='text.secondary' mt={2} mb={1}>
          Body
        </Typography>

        {open && <Editor rteRef={rteRef} contents={body} editable={true} />}

        <Divider sx={{ my: 3 }} />

        <Typography variant='body2' color='text.secondary' mb={1}>
          Sign Off
        </Typography>

        {open && <Editor rteRef={signOffRef} contents={signOff} editable={true} />}

        {error && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box mt={2} display='flex' justifyContent='flex-end' gap={2}>
          <Button variant='outlined' onClick={onClose}>
            Cancel
          </Button>
          <Button variant='contained' color='primary' size='small' onClick={handleUpdate} sx={tablePrimaryButtonSx}>
            Update
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
